/*
 * TTTech nerve-management-system-frontend
 * Copyright(c) 2025 TTTech Industrial Automation AG.
 *
 * ALL RIGHTS RESERVED.
 * Usage of this software, including source code, netlists, documentation,
 * is subject to restrictions and conditions of the applicable license
 * agreement with TTTech Industrial Automation AG or its affiliates.
 *
 * All trademarks used are the property of their respective owners.
 * TTTech Industrial Automation AG and its affiliates do not assume any liability
 * arising out of the application or use of any product described or shown
 * herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 * make changes, at any time, in order to improve reliability, function or
 * design.
 *
 * Contact Information:
 * support@tttech-industrial.com
 * TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 */

import { cloneDeep, isEqual, uniq } from 'lodash-es';
import { defineStore } from 'pinia';
import i18n from '@/i18n';
import NodeModel from '@/model/node/node.model';
import NodeBasicInfoModel from '@/model/node/nodeBasicInfo.model';
import {
  NodesApiService,
  ProductionModeApiService,
  WorkloadsApiService,
  ConfigurationsApiService,
} from '@/services/api';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import NodeTreeHelper from '@/store/helpers/node-tree.helper';
import mqtt from '@/plugins/mqtt';
import { isNoPrereleasedVersionGreaterThanOrEqualTo, getKeyByValue, checkUrlAndRedirect } from '@/helpers/shared';
import useVolumesStore from '@/store/modules/pinia/volumes';
import DeployedComposeWorkloadModel from '@/model/node/sub-models/deployed-compose-workload.model';
import DeployedWorkloadModel, { STATS, PROPERTY_LIST_NAMES } from '@/model/node/sub-models/deployed-workload.model';
import DeployedComposeWorkloadServicesModel from '@/model/node/sub-models/deployed-compose-workload-services.model';
import { TYPE_OF_WORKLOADS, STATUSES } from '@/constants';
import WorkloadsHelper from '@/store/helpers/workloads.helper';
import router from '@/router';
import logger from '@/plugins/logger';

const NODE_LOGGING_MONITORING_OPTION_SUPPORTED_SINCE_VERSION = '2.3.0';
const NODE_LOGGING_MONITORING_SYSTEM_LOG_FILTER_SUPPORTED_SINCE_VERSION = '3.1.0';

// Serial number of the node whose Logs tab last loaded monitoring/logging settings, if any.
let logsTabVisitedSerialNumber = null;

const setPropDeployedWorkloadServices = (state, values) => {
  const { services, configurationUpdateInfo } = values;
  if (state.selectedDeployedWorkload && services) {
    const newServices = [];
    state.selectedDeployedWorkload.services = state.selectedDeployedWorkload.services || [];
    services.forEach((service) => {
      let configurationInfo = {};
      let configurationStatus = {};
      if (configurationInfo) {
        if (Array.isArray(configurationUpdateInfo)) {
          configurationInfo = configurationUpdateInfo.find((config) => config.serviceName === service.serviceName);
          configurationStatus = {
            ...(configurationInfo && { timestamp: configurationInfo.timestamp }),
            ...(configurationInfo && { containerPath: configurationInfo.path }),
            ...(configurationInfo && { volumeName: configurationInfo.name }),
            ...(configurationInfo && { status: configurationInfo.status }),
            ...(configurationInfo && { user: configurationInfo.user }),
            ...(configurationInfo && { message: configurationInfo.message }),
          };
        } else {
          configurationInfo = {
            ...(configurationUpdateInfo && {
              restartOnConfigurationUpdate: configurationUpdateInfo.restartOnConfigurationUpdate,
            }),
            ...(configurationUpdateInfo &&
              configurationUpdateInfo.configurationVolumes && {
                path: configurationUpdateInfo.configurationVolumes[0].path,
              }),
            ...(configurationUpdateInfo &&
              configurationUpdateInfo.configurationVolumes && {
                name: configurationUpdateInfo.configurationVolumes[0].name,
              }),
            serviceName: service.serviceName,
          };
          configurationStatus = {
            ...(configurationInfo && { timestamp: configurationInfo.timestamp }),
            ...(configurationInfo && { containerPath: configurationInfo.path }),
            ...(configurationInfo && { volumeName: configurationInfo.name }),
            ...(configurationInfo && { status: configurationInfo.status }),
            ...(configurationInfo && { user: configurationInfo.user }),
            ...(configurationInfo && { message: configurationInfo.message }),
          };
        }
      }
      service = {
        ...service,
        configurationUpdateInfo: configurationInfo,
        configurationUpdateStatus: configurationStatus,
      };
      newServices.push(
        new DeployedComposeWorkloadServicesModel({
          ...state.selectedDeployedWorkload.services.find((s) => s.serviceName === service.serviceName),
          ...service,
        }),
      );
    });
    state.selectedDeployedWorkload.services = newServices;
  }
};

const parseAndSetEnvVarsAndPorts = async (state, payload) => {
  const { envVars, ports } = payload;
  const environmentVariables = WorkloadsHelper.parseEnvVars(envVars);
  const portList = WorkloadsHelper.parsePorts(ports);
  if (state.selectedDeployedWorkload) {
    state.selectedDeployedWorkload['_environmentVariables'] = environmentVariables;
    state.selectedDeployedWorkload['_portList'] = portList;
  }
};

/**
 * Set CPU and RAM workload stats on the selected node in the state tree
 * @param {Object} store - Pinia store instance
 * @param {Object} payload - Object containing stats and deviceId
 * @param {Object} payload.stats - Workload stats
 * @param {string|number} payload.deviceId - Device ID
 */
const setCpuRamWorkloadOnSelectedNode = (store, { stats, deviceId }) => {
  if (!store.selectedNode) {
    return;
  }
  const workloadStats = Object.assign(stats, { deviceId });
  const updatedNode = new NodeModel({
    ...store.selectedNode,
    workloadStats: [workloadStats],
  });
  store.nodesMap.set(store.selectedNodeSerialNumber, updatedNode);
};

// Log level keys whose value is a `{ level, options }` object rather than a plain scalar.
const MQTT_LOG_LEVEL_KEYS = ['codesysLogLevel', 'virtualizationLogLevel', 'nodeManagementLogLevel'];

/**
 * Backend `options` arrays for log-level keys can contain duplicate entries (e.g. "error"
 * listed twice). Vuetify's v-select matches the selected value against every item in the
 * list, so duplicates make the selected value render twice (e.g. "error error"). Dedupe
 * here so the UI never sees duplicated options.
 * @param {Object} loggingMonitoring - flat loggingMonitoring payload
 * @returns {Object} same shape with deduplicated `options` arrays
 */
const dedupeLogLevelOptions = (loggingMonitoring) => {
  const sanitized = { ...loggingMonitoring };
  MQTT_LOG_LEVEL_KEYS.forEach((key) => {
    if (sanitized[key]?.options) {
      sanitized[key] = { ...sanitized[key], options: uniq(sanitized[key].options) };
    }
  });
  return sanitized;
};

/**
 * Merge a flat mqtt loggingMonitoring payload into the store's flat monitoringAndLoggingSettings,
 * then recompute the initial values snapshot (a mqtt push discards any unsaved local edits).
 * @param {Object} store - Pinia store instance
 * @param {Object} flatChanges - flat loggingMonitoring payload from the mqtt event
 */
const applyMqttLoggingMonitoringChanges = (store, flatChanges) => {
  const settings = store.monitoringAndLoggingSettings || {};
  const updated = { ...settings };
  Object.entries(flatChanges).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    updated[key] = MQTT_LOG_LEVEL_KEYS.includes(key) ? { ...settings[key], level: value } : value;
  });
  store.monitoringAndLoggingSettings = updated;
  store.monitoringAndLoggingSettingsInitialValues = cloneDeep(updated);
};

/**
 * Set selected node and update feature flags based on firmware version
 * @param {Object} store - Pinia store instance
 * @param {Object} nodeData - Node data to set
 * @returns {Object} Updated state properties
 */
const setSelectedNode = (store, nodeData) => {
  const existingNode = store.nodesMap.get(nodeData.serialNumber);
  const mergedNodeData = {
    ...nodeData,
    dnaNodeStatus: nodeData.dnaNodeStatus || existingNode?.dnaNodeStatus,
    dnaWorkloadStatus: nodeData.dnaWorkloadStatus || existingNode?.dnaWorkloadStatus,
  };
  const nodeModel = new NodeModel(mergedNodeData);
  store.selectedNodeSerialNumber = nodeData.serialNumber;
  store.nodesMap.set(nodeData.serialNumber, nodeModel);
  store.isNewComposeSupported = store.selectedNode
    ? isNoPrereleasedVersionGreaterThanOrEqualTo(store.selectedNode.currentFWVersion, '2.7.0')
    : false;

  store.isRemoveUnusedDockerImagesSupported = store.selectedNode
    ? isNoPrereleasedVersionGreaterThanOrEqualTo(store.selectedNode.currentFWVersion, '2.10.0')
    : false;

  return store;
};

const setTreeNodeDevice = (store, { device, statsInfo, setSelectedNode }) => {
  // check if selected node has the same serial number as the device from the mqtt message
  if (store.selectedNode?.serialNumber !== device.serial) {
    return;
  }

  if (setSelectedNode) {
    store.isNewComposeSupported = store.selectedNode
      ? isNoPrereleasedVersionGreaterThanOrEqualTo(store.selectedNode.currentFWVersion, '2.7.0')
      : false;
    store.isRemoveUnusedDockerImagesSupported = store.selectedNode
      ? isNoPrereleasedVersionGreaterThanOrEqualTo(store.selectedNode.currentFWVersion, '2.10.0')
      : false;
  }
  // we are doing assign because we don't get whole node on every device update
  const updatedNode = store.selectedNode ? new NodeModel({ ...store.selectedNode }) : new NodeModel({});
  if (statsInfo) {
    const workloadStats = statsInfo.workloads;
    delete statsInfo.workloads;
    Object.assign(
      updatedNode,
      new NodeModel({
        ...updatedNode,
        ...device,
        ...statsInfo,
        workloadStats,
      }),
    );
  } else {
    Object.assign(updatedNode, new NodeModel({ ...updatedNode, ...device }));
  }
  store.nodesMap.set(store.selectedNodeSerialNumber, updatedNode);
};

const setPropDeployedComposeWorkload = (state, { propName, values }) => {
  if (state.selectedDeployedWorkload) {
    if (propName === 'services' && values.services) {
      values.services.forEach((serviceWithStatus) => {
        state.selectedDeployedWorkload.services.forEach((service, index) => {
          if (service.containerName === serviceWithStatus.containerName && serviceWithStatus.state) {
            state.selectedDeployedWorkload.services[index].status = getKeyByValue(
              STATUSES,
              serviceWithStatus.state,
            ).toLowerCase();
          }
          if (service.containerName === serviceWithStatus.containerName && serviceWithStatus.timestamp) {
            state.selectedDeployedWorkload.services[index].lastStateChange = new Date(
              serviceWithStatus.timestamp,
            ).toLocaleString('en-GB');
          }
        });
      });
    }
    if (propName === 'configurationUpdateStatus' && state.selectedDeployedWorkload.services) {
      state.selectedDeployedWorkload.services.map((service) => {
        if (service.serviceName === values.serviceName) {
          service.configurationUpdateStatus = {
            timestamp: values.timestamp,
            containerPath: values.containerPath,
            volumeName: values.volumeName,
            status: values.status,
            user: values.user,
            message: values.message,
          };
        }
        return service;
      });
    }
  }
};

const setTreeNodeDeviceWorkloads = (store, { device, workloads }) => {
  // Update workloads for the node
  if (!store.selectedNode) {
    return;
  }
  const updatedDevices = NodeTreeHelper.addOrUpdateAndMergeDeployedWls(workloads, store.selectedNode).map((d) =>
    (d._type === TYPE_OF_WORKLOADS.COMPOSE || d._type === 'Docker Compose') && store.isNewComposeSupported
      ? new DeployedComposeWorkloadModel(d)
      : new DeployedWorkloadModel(d),
  );

  // Also update selectedNode's devices if it matches
  if (
    store.selectedNode &&
    (store.selectedNode.serialNumber === (device.serialNumber || device.serial) || store.selectedNode.id === device.id)
  ) {
    const updatedNode = new NodeModel({
      ...store.selectedNode,
      devices: updatedDevices,
    });
    store.nodesMap.set(store.selectedNodeSerialNumber, updatedNode);
  }

  // Update selectedDeployedWorkload if it matches one of the updated workloads
  if (store.selectedDeployedWorkload && store.selectedDeployedWorkload.id) {
    const updatedWorkload = updatedDevices.find((w) => w.id === store.selectedDeployedWorkload.id);
    if (updatedWorkload) {
      // Preserve service statuses set by MQTT all_services_status — the raw node data
      // does not carry them, so a plain replacement would wipe out those updates.
      if (updatedWorkload.services?.length && store.selectedDeployedWorkload.services?.length) {
        updatedWorkload.services.forEach((service) => {
          const existing = store.selectedDeployedWorkload.services.find(
            (s) => s.containerName === service.containerName || s.serviceName === service.serviceName,
          );
          if (existing) {
            service.status = existing.status || service.status;
            service.lastStateChange = existing.lastStateChange || service.lastStateChange;
            // Preserve configurationUpdateInfo and configurationUpdateStatus to avoid losing data
            service.configurationUpdateInfo = existing.configurationUpdateInfo || service.configurationUpdateInfo;
            service.configurationUpdateStatus = existing.configurationUpdateStatus || service.configurationUpdateStatus;
          }
        });
      }
      store.selectedDeployedWorkload = updatedWorkload;
    }
  }
};

function collectUniqueMetaKeys(nodesMap, nodeKey) {
  const keys = new Set();
  for (const node of nodesMap.values()) {
    (node[nodeKey] || []).forEach((item) => keys.add(item.key));
  }
  return [...keys];
}

const useNodesStore = defineStore('nodes', {
  state: () => ({
    nodesMap: new Map(),
    selectedNodeSerialNumber: '',
    nodeData: new NodeBasicInfoModel({}),
    timezoneName: '',
    oldNodeData: {},
    isUpdate: false,
    isNodeLogsSaveEnabled: false,
    isNodeNameEditDirty: false,
    isDnaTargetConfigurationDirty: false,
    isNodeLabelsDirty: false,
    isNodeLogsTimerChanged: false,
    isNewComposeSupported: false,
    isRemoveUnusedDockerImagesSupported: false,
    isRemoveUnusedDockerImagesDisabled: false,
    monitoringAndLoggingSettings: {},
    monitoringAndLoggingSettingsInitialValues: {},
    queryParams: {},
    cancelTokenDnaStatus: {},
    cancelTokenNodeDnaStatus: {},
    versionIdOfSelectedWl: '',
    selectedDeployedWorkload: new DeployedWorkloadModel({
      id: '',
      name: ' ',
      status: '',
      stats: {},
    }),
    monitoringAndLoggingSettingsDialog: false,
    lastState: '',
    cancelToken: null,
    cancelTokenStartSending: null,
    isNodeDataAvailable: false,
    isUpdateResourcesInProgress: false,
    restartMqttTimeout: null,
    isRestartMqttInProgress: false,
  }),

  getters: {
    nodes: (state) => Array.from(state.nodesMap.values()),
    selectedNode: (state) => state.nodesMap.get(state.selectedNodeSerialNumber) || null,
    uniqueDnaNodeMetadataKeys: (state) => collectUniqueMetaKeys(state.nodesMap, 'dnaNodeMetadata'),
    uniqueDnaWorkloadMetadataKeys: (state) => collectUniqueMetaKeys(state.nodesMap, 'dnaWorkloadMetadata'),
  },

  actions: {
    async fetch() {
      const { nodes } = await NodesApiService.fetchNodes();
      this.nodesMap = new Map(
        nodes.map((node) => {
          const nodeModel = new NodeModel(node);
          return [nodeModel.serialNumber, nodeModel];
        }),
      );
    },
    setSelectedNode(nodeData) {
      if (!nodeData) {
        this.selectedNodeSerialNumber = '';
        return;
      }
      setSelectedNode(this, nodeData);
    },
    clearSelectedNode() {
      this.selectedNodeSerialNumber = '';
    },
    clearNodeState() {
      this.nodeData = new NodeBasicInfoModel({});
      this.timezoneName = '';
    },
    async getNodeById(nodeId) {
      const nodeData = nodeId === 'new' ? {} : await NodesApiService.getNodeById(nodeId, { fetchFreshNodeData: false });
      this.nodeData = new NodeBasicInfoModel(nodeData);
      this.timezoneName = this.nodeData.timezone.name;
      this.oldNodeData = cloneDeep(this.nodeData);
    },
    setIsUpdateFlag(isUpdate) {
      this.isUpdate = isUpdate;
    },
    async updateNode({ nodeData, rc }) {
      const utilsStore = useUtilsStore();
      try {
        this.nodeData.remoteConnections = rc;
        nodeData.remoteConnections = rc;
        await NodesApiService.updateNode(nodeData);

        setSelectedNode(this, { ...this.selectedNode, ...this.nodeData });
      } catch (e) {
        if (e && e.response && e.response.data) {
          const { data } = e.response;
          const err = data[0].errorCode ? data[0].errorCode : 'nerve_unexp_server_003';
          utilsStore.showCustomToast({
            text: `errorMessages.${err}`,
            color: 'red',
            showClose: true,
          });
          throw e.response.data;
        }
        throw e;
      }
    },
    async createNewNode({ nodeData, rc }) {
      const utilsStore = useUtilsStore();
      try {
        nodeData.remoteConnections = rc;
        const response = await NodesApiService.createNewNode(nodeData);
        this.nodeData = new NodeBasicInfoModel(response);
        this.timezoneName = this.nodeData.timezone.name;

        const newNode = new NodeModel(this.nodeData, true);
        this.nodesMap.set(newNode.serialNumber, newNode);
      } catch (e) {
        const { data } = e.response;
        const err = data[0].errorCode ? data[0].errorCode : 'nerve_unexp_server_002';
        utilsStore.showCustomToast({
          text: `errorMessages.${err}`,
          color: 'red',
          showClose: true,
        });
        throw e.response.data;
      }
    },
    async remove(serialNumber) {
      await NodesApiService.remove(serialNumber);
      this.nodesMap.delete(serialNumber);
      if (this.selectedNodeSerialNumber === serialNumber) {
        this.selectedNodeSerialNumber = '';
        this.nodeData = new NodeModel({});
      }
      const utilsStore = useUtilsStore();
      utilsStore.showCustomToast({
        text: 'nodes.list.deleteItem',
        color: 'success',
        showClose: true,
      });
    },
    async fetchNodesForDeploy(parameters = {}) {
      // Helper function to handle sorting
      function handleOrder1({ sortBy, sortDesc }) {
        if (!sortBy || !sortDesc) {
          return { created: 'desc' };
        }
        if (!sortBy.length || !sortDesc.length) {
          return { created: 'desc' };
        }
        return { [sortBy[0]]: sortDesc[0] ? 'desc' : 'asc' };
      }
      const params = {
        filterBy: { name: parameters.search },
        limit: parameters.itemsPerPage || 10,
        page: parameters.page || 1,
        order: handleOrder1(parameters),
      };

      const workloadId = parameters?.workload?.workloadId;
      const versionId = parameters?.workload?.versionId;

      try {
        const response = await NodesApiService.fetchDeployNodes({ workloadId, versionId, params });
        const nodes = response.data.map((node) => new NodeModel(node));
        return { nodes, count: response.count };
      } catch (error) {
        console.error('Error fetching nodes for deploy:', error);
        throw error;
      }
    },

    setNodeLogsSaveEnabled(value) {
      this.isNodeLogsSaveEnabled = value;
    },
    setNodeNameEditDirty(value) {
      this.isNodeNameEditDirty = value;
    },
    setDnaTargetConfigurationDirty(value) {
      this.isDnaTargetConfigurationDirty = value;
    },
    setNodeLabelsDirty(value) {
      this.isNodeLabelsDirty = value;
    },
    setNodeLogsTimerChanged(value) {
      this.isNodeLogsTimerChanged = value;
    },
    doesNodeSupportsLoggingAndMonitoring() {
      if (!this.selectedNode) {
        return false;
      }
      const nodeVersion = this.selectedNode.currentFWVersion;
      return isNoPrereleasedVersionGreaterThanOrEqualTo(
        nodeVersion,
        NODE_LOGGING_MONITORING_OPTION_SUPPORTED_SINCE_VERSION,
      );
    },
    doesNodeSupportsSystemLogFilter() {
      if (!this.selectedNode) {
        return false;
      }
      const nodeVersion = this.selectedNode.currentFWVersion;
      return isNoPrereleasedVersionGreaterThanOrEqualTo(
        nodeVersion,
        NODE_LOGGING_MONITORING_SYSTEM_LOG_FILTER_SUPPORTED_SINCE_VERSION,
      );
    },
    checkForNodeLogsChanges() {
      // timer has different comparison logic
      if (this.isNodeLogsTimerChanged) {
        this.setNodeLogsSaveEnabled(true);
        return;
      }
      const isChanged = !isEqual(this.monitoringAndLoggingSettingsInitialValues, this.monitoringAndLoggingSettings);
      this.setNodeLogsSaveEnabled(isChanged);
    },
    async getCurrentMonitoringAndLoggingSettings(param) {
      // `supported: false` signals there is no real logging/monitoring data - hides all related UI.
      const defaultParams = { supported: false };

      logsTabVisitedSerialNumber = param.serialNumber;

      try {
        if (param.connectionStatus) {
          const { loggingMonitoring } = await NodesApiService.getMonitoringAndLoggingSettingsV2(param.serialNumber);
          const sanitizedLoggingMonitoring = dedupeLogLevelOptions(loggingMonitoring);
          this.monitoringAndLoggingSettings = sanitizedLoggingMonitoring;
          this.monitoringAndLoggingSettingsInitialValues = cloneDeep(sanitizedLoggingMonitoring);
        } else {
          this.monitoringAndLoggingSettings = defaultParams;
          this.monitoringAndLoggingSettingsInitialValues = cloneDeep(defaultParams);
        }
      } catch (error) {
        this.monitoringAndLoggingSettings = defaultParams;
        this.monitoringAndLoggingSettingsInitialValues = cloneDeep(defaultParams);
        throw error;
      }
    },
    async selectDeployedWorkload(id) {
      const workload = this.selectedNode?.devices.find((device) => device.id.toString() === id.toString()) || null;
      if (workload) {
        const { values } = await NodesApiService.getDataFromNode({
          dataId: 'workload',
          serialNumber: this.selectedNode.serialNumber,
          data: workload.id,
        });
        WorkloadsHelper.setStartEmittingFlag(false);
        this.selectedDeployedWorkload = workload;
        if (this.selectedDeployedWorkload) {
          this.selectedDeployedWorkload['timestamp'] = values.statusChangedTimestamp;
        }
        if (workload.type !== TYPE_OF_WORKLOADS.COMPOSE || !this.isNewComposeSupported) {
          if (this.selectedDeployedWorkload) {
            this.selectedDeployedWorkload['configurationUpdateInfo'] = values.configurationUpdateInfo;
          }
        }
        if (
          workload.type === TYPE_OF_WORKLOADS.DOCKER ||
          (workload.type === TYPE_OF_WORKLOADS.COMPOSE && !this.isNewComposeSupported)
        ) {
          parseAndSetEnvVarsAndPorts(this, { envVars: values.envVars, ports: values.ports });
        }
        if (workload.type === TYPE_OF_WORKLOADS.COMPOSE && this.isNewComposeSupported) {
          setPropDeployedWorkloadServices(this, {
            services: values.servicesInformation,
            configurationUpdateInfo: values.configurationUpdateInfo,
          });
        }
        if (workload.type === TYPE_OF_WORKLOADS.VM) {
          const resources = {
            memory: values.resources?.memory?.current || 0,
            cpu: values.resources?.cpu?.current || 0,
          };
          if (this.selectedDeployedWorkload) {
            this.selectedDeployedWorkload._resources = resources;
          }
        }
        const isVersionSatisfying = isNoPrereleasedVersionGreaterThanOrEqualTo(
          this.selectedNode.currentFWVersion,
          '2.5.0',
        );
        if (isVersionSatisfying && workload.type !== TYPE_OF_WORKLOADS.CODESYS) {
          WorkloadsHelper.setSelectedWorkload(workload);
          WorkloadsHelper.setSelectedNode(this.selectedNode);
        }
      }
    },
    async applyWorkloadConfiguration(formData) {
      await ConfigurationsApiService.applyWorkloadConfiguration(
        this.selectedNode.serialNumber,
        this.selectedDeployedWorkload.id,
        formData,
      );
    },
    /**
     * Sending the changed resources to the backend
     * @param {*} param0
     * @param {*} data
     */
    async updateResources(data) {
      try {
        await WorkloadsApiService.updateResources(data);
        this.isUpdateResourcesInProgress = true;
        useUtilsStore().showCustomToast({
          text: 'workloadManagement.updateResourcesMessage',
          color: 'success',
          showClose: true,
        });
      } catch (err) {
        this.isUpdateResourcesInProgress = false;
        useUtilsStore().showCustomToast({
          text: err.response.data[0].message,
          color: 'red',
          showClose: true,
        });
        this.vmResourcesResponse = { status: 'FAIL', message: err.response.data.msg };
        throw err;
      }
    },
    async removeUnusedDockerImages({ name, serialNumber }) {
      const utilsStore = useUtilsStore();
      try {
        const timeout = setTimeout(() => {
          this.isRemoveUnusedDockerImagesDisabled = true;
        }, 500);
        await NodesApiService.deleteUnusedDockerImages({ serialNumber });
        utilsStore.showCustomToast({
          text: 'nodeConfiguration.removeUnusedDockerImagesSuccessMessage',
          textParameters: `${name}[${serialNumber}]`,
          color: 'success',
          showClose: true,
        });
        clearTimeout(timeout);
        this.isRemoveUnusedDockerImagesDisabled = false;
      } catch (error) {
        logger.debug(error);
        this.isRemoveUnusedDockerImagesDisabled = false;
      }
    },
    setCancelTokenStartSending(cancelToken) {
      this.cancelTokenStartSending = cancelToken;
    },
    /**
     * @description Clear exchange interval - eg. on destroyed component
     */
    clearExchangeInterval() {
      // Also clear any pending restartMqttEvents timeout
      if (this.restartMqttTimeout) {
        clearTimeout(this.restartMqttTimeout);
        this.restartMqttTimeout = null;
      }
      // Clear the in-progress flag
      this.isRestartMqttInProgress = false;
      NodeTreeHelper.clearExchangeInterval();
    },
    /**
     * @description Restart mqtt events - eg. on mount of a component
     * @param selectedNode
     * @returns {Promise<void>}
     */
    // Fetch selected node with fresh data from API (with workloads)
    async restartMqttEvents({ selectedNode, workloadControlPermission, isKeepEmitting = true }) {
      if (!selectedNode.id || !selectedNode.isOnline()) {
        return;
      }

      // Store the serial number of the node we're fetching for validation later
      const requestedSerialNumber = selectedNode.serialNumber;

      // If already in progress, cancel any pending operations and clear the flag
      if (this.isRestartMqttInProgress) {
        // Clear any pending timeout
        if (this.restartMqttTimeout) {
          clearTimeout(this.restartMqttTimeout);
          this.restartMqttTimeout = null;
        }
        // Cancel any pending API requests
        if (this.cancelToken && this.cancelToken.cancel) {
          this.cancelToken.cancel('New node selection - canceling previous requests');
        }
        if (this.cancelTokenStartSending && this.cancelTokenStartSending.cancel) {
          this.cancelTokenStartSending.cancel('New node selection - canceling previous requests');
        }
        if (this.cancelTokenDnaStatus && this.cancelTokenDnaStatus.cancel) {
          this.cancelTokenDnaStatus.cancel('New node selection - canceling previous requests');
        }
        if (this.cancelTokenNodeDnaStatus && this.cancelTokenNodeDnaStatus.cancel) {
          this.cancelTokenNodeDnaStatus.cancel('New node selection - canceling previous requests');
        }
      }

      // Set the flag to prevent concurrent executions
      this.isRestartMqttInProgress = true;

      // Clear any pending timeout to prevent race conditions when clicking nodes rapidly
      if (this.restartMqttTimeout) {
        clearTimeout(this.restartMqttTimeout);
        this.restartMqttTimeout = null;
      }

      NodeTreeHelper.setStartEmittingFlag(false);

      try {
        await new Promise((resolve, reject) => {
          this.restartMqttTimeout = setTimeout(async () => {
            try {
              await NodeTreeHelper.startEmitting({ newSelectedDevice: selectedNode });
              const apiProvidedDevice = await NodeTreeHelper.fetchNodeDetails(selectedNode, workloadControlPermission);
              // Only update if this is still the selected online node (prevent stale data from overwriting)
              if (
                this.selectedNode &&
                this.selectedNode.serialNumber === requestedSerialNumber &&
                this.selectedNode.isOnline()
              ) {
                setSelectedNode(this, apiProvidedDevice);
                if (this.selectedNode) {
                  await mqtt.subscribeTo('node', this.selectedNode);
                  NodeTreeHelper.setSelectedNode(this.selectedNode);
                }
              }

              this.restartMqttTimeout = null;
              resolve();
            } catch (error) {
              this.restartMqttTimeout = null;
              reject(error);
            }
          }, 100);
        });
        this.isNodeDataAvailable = true;

        if (isKeepEmitting) {
          await NodeTreeHelper.invokeMqttEvents();
        }
      } finally {
        // Always clear the flag when done
        this.isRestartMqttInProgress = false;
      }
    },
    async updateMonitoringAndLoggingSettingsV2(payload) {
      await NodesApiService.monitoringAndLoggingSettingsV2(payload);
      // The store's settings already reflect what was just saved - use them as the new baseline.
      this.monitoringAndLoggingSettingsInitialValues = cloneDeep(this.monitoringAndLoggingSettings);
    },
    setQueryParams(params) {
      this.queryParams = params;
    },
    async setProductionMode(data) {
      const utilsStore = useUtilsStore();
      const res = await ProductionModeApiService.setProductionMode(data);
      if (this.selectedNode && this.selectedNode.serialNumber === data.serialNumber) {
        const updatedNode = new NodeModel({
          ...this.selectedNode,
          productionMode: !!data.productionMode,
        });
        this.nodesMap.set(this.selectedNodeSerialNumber, updatedNode);
      }
      // TODO: Remove this once the new indicator topic has been implemented.
      const nodeFromMap = this.nodesMap.get(data.serialNumber);
      if (nodeFromMap) {
        const updatedNodeForMap = new NodeModel({
          ...nodeFromMap,
          productionMode: !!data.productionMode,
        });
        this.nodesMap.set(data.serialNumber, updatedNodeForMap);
      }
      utilsStore.showCustomToast({
        text: data.productionMode
          ? 'nodes.productionMode.productionModeOnMessage'
          : 'nodes.productionMode.productionModeOffMessage',
        color: 'green',
      });
      return res;
    },
    setCancelTokenDnaStatus(cancelToken) {
      this.cancelTokenDnaStatus = cancelToken;
    },
    setCancelTokenNodeDnaStatus(cancelToken) {
      this.cancelTokenNodeDnaStatus = cancelToken;
    },

    // TODO: Remove this once the new indicator topic has been implemented.
    updateDnaWorkloadStatus({ serialNumber, status }) {
      if (!serialNumber || !status) {
        return;
      }
      if (this.selectedNode && this.selectedNode.serialNumber === serialNumber) {
        const updatedNode = new NodeModel({
          ...this.selectedNode,
          dnaWorkloadStatus: status,
        });
        this.nodesMap.set(this.selectedNodeSerialNumber, updatedNode);
      }
      const nodeFromMap = this.nodesMap.get(serialNumber);
      if (nodeFromMap) {
        const updatedNodeForMap = new NodeModel(
          {
            ...nodeFromMap,
            dnaWorkloadStatus: status,
          },
          true,
        );
        this.nodesMap.set(serialNumber, updatedNodeForMap);
      }
    },

    setIsNewComposeSupported(value) {
      this.isNewComposeSupported = value;
    },

    setCancelToken(cancelToken) {
      this.cancelToken = cancelToken;
    },

    async setCommand({ params, command }) {
      if (!params.serviceName) {
        const currentStatus = getKeyByValue(STATUSES, command.updateTo);
        if (this.selectedDeployedWorkload) {
          this.selectedDeployedWorkload['_currentStatus'] = currentStatus;
        }
      }
      await WorkloadsApiService.setCommand(params);
    },

    setLastState(lastState) {
      this.lastState = lastState;
    },

    /**
     * Set version id of selected wl
     * @param commit
     * @param {*} versionId
     */
    async setVersionIdOfSelectedWl(versionId) {
      this.versionIdOfSelectedWl = versionId;
    },

    updateMonitoringAndLoggingParams({ commit }, payload) {
      // only if dialog is open state will be updated
      if (this.monitoringAndLoggingSettingsDialog) {
        // TODO: This function is not available in the integration. Please check.
        commit('SHOW_MONITORING_LOGGING_SETTINGS_DIALOG', payload.params.values);
      }
    },
    async revertState() {
      if (this.selectedDeployedWorkload && this.lastState) {
        this.selectedDeployedWorkload['_currentStatus'] = this.lastState;
      }
    },

    serviceActionClicked(service) {
      const utilsStore = useUtilsStore();
      utilsStore.showApplyWorkloadConfigurationDialog({
        title: this.selectedDeployedWorkload.name,
        service,
      });
    },

    setIsUpdateResourcesInProgress(value) {
      this.isUpdateResourcesInProgress = value;
    },

    clearSelectedDeployedWorkload() {
      this.selectedDeployedWorkload = new DeployedWorkloadModel({
        id: '',
        name: ' ',
        status: '',
        stats: {},
      });
    },

    /**
     * @description Dedicated action for mqtt oblo/all/admin/svc/notifier/evt topic
     * @param payload - mqtt message payload
     */
    mqttSelectedNodeUpdated(payload) {
      const utilsStore = useUtilsStore();
      const volumesStore = useVolumesStore();
      switch (payload.name) {
        case 'NodePersistentDataEvent':
        case 'NodeDataEvent': {
          const deployedWorkload = this.selectedDeployedWorkload;
          switch (payload.params.dataId) {
            case 'workload':
              setTreeNodeDeviceWorkloads(this, {
                device: { serial: payload.params.serial },
                workloads: [payload.params.values.workload],
              });
              if (deployedWorkload && deployedWorkload.id === payload?.params?.values?.workload?.deviceId) {
                const stats = {
                  propName: 'timestamp',
                  propValue: payload.params.values.workload.statusChangedTimestamp,
                };
                if (this.selectedDeployedWorkload) {
                  Object.entries(stats).forEach(([key, value]) => {
                    this.selectedDeployedWorkload.stats[key] = value;
                  });
                }
              }
              break;
            case 'wan_ip_address':
            case 'system_stats': {
              setTreeNodeDevice(this, {
                device: { serial: payload.params.serial },
                statsInfo: payload.params.values,
              });
              break;
            }
            case 'monitoring_and_logging_status': {
              // the status is only updated for the selected node
              if (this.selectedNode && payload.params.serial === this.selectedNode.serialNumber) {
                // TODO: The mutation for this function does not exist in the integration. Please check.
                // dispatch('nodes/update_monitoring_and_logging_params', payload, { root: true });
              }
              break;
            }

            case `${deployedWorkload.workloadId}_${deployedWorkload.versionId}_stats`:
              Object.entries(payload.params.values).forEach(([key, value]) => {
                const statsObj = {};
                statsObj[key] = value;
                if (this.selectedDeployedWorkload) {
                  Object.entries(statsObj).forEach(([key, value]) => {
                    this.selectedDeployedWorkload.stats[key] = value;
                  });
                }
              });
              break;
            case `${deployedWorkload.workloadId}_${deployedWorkload.versionId}_all_services_status`:
              setPropDeployedComposeWorkload(this, {
                propName: 'services',
                values: payload.params.values,
              });
              break;
            case 'RemoteConnectionSettings': {
              if (this.selectedNode) {
                const updatedNodeWithRc = new NodeModel({
                  ...this.selectedNode,
                  rcApproval: payload?.params?.values?.approve || 0,
                });
                this.nodesMap.set(this.selectedNodeSerialNumber, updatedNodeWithRc);
              }
              break;
            }
          }
          break;
        }
        case 'StatusOfChangingVmResource':
          this.isUpdateResourcesInProgress = false;
          // Show a toast notification for the status
          utilsStore.showCustomToast({
            text: payload.params.message,
            color: payload.params.status === 'SUCCESS' ? 'success' : 'error',
            showClose: true,
          });
          break;
        case 'VolumeBackupStatus':
          volumesStore.setVolumeBackupStatus(payload.params);
          break;
      }
    },
    async reboot({ serialNumber }) {
      return NodesApiService.reboot(serialNumber);
    },
    mqttSelectedNodeDeployedWlUpdated(payload) {
      const utilsStore = useUtilsStore();
      // description Dedicated action for `oblo/${userId}/gtw/${serialNumber}/ovdm/evt`
      switch (payload.name) {
        case 'device_property_changed': {
          const stats = getKeyByValue(STATS, payload.params.property_name);
          if (stats) {
            const statsName = stats.toLowerCase();
            const key = statsName;
            const statsObj = {};
            statsObj[key] = payload.params.property_value;
            // to update cpu and ram of the wl in the table view
            setCpuRamWorkloadOnSelectedNode(this, {
              stats: statsObj,
              deviceId: payload.params.device_id,
            });
            // for the older nodes to update stats property in the wl model
            // this property shows cpu and ram when it is clicked on the wl
            if (this.selectedDeployedWorkload && this.selectedDeployedWorkload.id === statsObj.deviceId) {
              Object.entries(statsObj).forEach(([key, value]) => {
                this.selectedDeployedWorkload.stats[key] = value;
              });
            }
          }
          if (payload.params.device_id !== this.selectedDeployedWorkload.id) {
            return;
          }

          if (payload.params.property_name === PROPERTY_LIST_NAMES.MESSAGE) {
            if (this.selectedDeployedWorkload) {
              this.selectedDeployedWorkload['message'] = { message: payload.params.property_value };
            }
          }
          if (payload.params.property_name === PROPERTY_LIST_NAMES.STATE) {
            const currentStatus = getKeyByValue(STATUSES, payload.params.property_value).toLowerCase();

            if (this.selectedDeployedWorkload) {
              this.selectedDeployedWorkload['_currentStatus'] = currentStatus;
            }
          }
          if (payload.params.property_name === PROPERTY_LIST_NAMES.CONFIGURATION_UPDATE_STATUS) {
            if (this.selectedDeployedWorkload.type !== TYPE_OF_WORKLOADS.COMPOSE || !this.isNewComposeSupported) {
              if (this.selectedDeployedWorkload) {
                this.selectedDeployedWorkload['_configurationUpdateStatus'] = payload.params.property_value;
              }
            } else {
              setPropDeployedComposeWorkload(this, {
                propName: 'configurationUpdateStatus',
                values: payload.params.property_value,
              });
            }
          }

          if (payload.params.property_name === PROPERTY_LIST_NAMES.VIDEO_OUTPUT) {
            if (this.selectedDeployedWorkload) {
              this.selectedDeployedWorkload['videoOutput'] = { videoOutput: payload.params.property_value };
            }
          }

          if (payload.params.property_name === PROPERTY_LIST_NAMES.MEMORY) {
            if (this.selectedDeployedWorkload) {
              this.selectedDeployedWorkload.resources['memory'] = payload.params.property_value;
            }
          }

          if (payload.params.property_name === PROPERTY_LIST_NAMES.CPU_NUMBER) {
            if (this.selectedDeployedWorkload) {
              this.selectedDeployedWorkload.resources['cpu'] = payload.params.property_value;
            }
          }
          break;
        }
        case 'EVENT_DEVICE_REMOVED': {
          utilsStore.showCustomToast({
            text: 'nodes.deployedWorkloadControl.undeployedSuccessfully',
            color: 'success',
            showClose: true,
          });
          const deviceIndex = this.selectedNode?.devices?.findIndex((d) => d.id === payload.params.device_id);
          if (this.selectedNode && deviceIndex !== -1) {
            const updatedDevices = [...this.selectedNode.devices];
            updatedDevices.splice(deviceIndex, 1);
            const updatedSelectedNode = new NodeModel({
              ...this.selectedNode,
              devices: updatedDevices,
            });
            this.nodesMap.set(this.selectedNodeSerialNumber, updatedSelectedNode);
          }
          if (WorkloadsHelper.selectedWorkload && WorkloadsHelper.selectedWorkload.id === payload.params.device_id) {
            WorkloadsHelper.clearExchangeInterval();
            if (
              window.location.pathname.includes('/device') &&
              window.location.pathname.includes(payload.params.device_id)
            ) {
              router.go(-1);
            }
          }
          break;
        }
      }
    },
    handleWorkloadEvents(message) {
      const messageParams = message.params || {};
      if (messageParams.code !== 1 || !messageParams.params || !messageParams.params.message) {
        return;
      }
      this.revertState();
    },
    /**
     * @description Dedicated action for mqtt nerve/node/update
     * @param serialNumber - serial number of the node to update
     * @param changes - object containing the changed properties of the node
     * @returns {Promise<void>}
     */
    async mqttAnyNodeUpdated({ serialNumber, changes }) {
      const nodeFromMap = this.nodesMap.get(serialNumber);
      if (!nodeFromMap) {
        return;
      }

      // loggingMonitoring isn't part of NodeModel, it belongs to monitoringAndLoggingSettings
      const { loggingMonitoring, ...nodeChanges } = changes;
      const updatedNode = new NodeModel({ ...nodeFromMap, ...nodeChanges });
      const newSerialNumber = nodeChanges.serialNumber || serialNumber;
      const serialNumberChanged = newSerialNumber !== serialNumber;

      // Update node in map
      if (serialNumberChanged) {
        this.nodesMap.delete(serialNumber);
      }
      this.nodesMap.set(newSerialNumber, updatedNode);

      // Update selected node reference if needed
      if (this.selectedNodeSerialNumber === serialNumber && serialNumberChanged) {
        this.selectedNodeSerialNumber = newSerialNumber;
      }

      const isSelectedNode = this.selectedNodeSerialNumber === newSerialNumber;
      // Ignore updates for nodes whose Logs tab hasn't been opened yet - there's nothing to merge into.
      if (isSelectedNode && loggingMonitoring && logsTabVisitedSerialNumber === newSerialNumber) {
        applyMqttLoggingMonitoringChanges(this, loggingMonitoring);
      }

      // Handle connection status changes for selected node
      if (!isSelectedNode || !changes.connectionStatus) {
        return;
      }

      const wasOnline = nodeFromMap.connectionStatus === 'online';
      const isNowOnline = changes.connectionStatus === 'online';
      const wentOnline = !wasOnline && isNowOnline;
      const wentOffline = wasOnline && !isNowOnline;

      if (wentOnline) {
        this.restartMqttEvents(
          {
            selectedNode: new NodeModel(this.selectedNode),
            workloadControlPermission: this.canAccess?.('UI_WORKLOAD_CONTROL:LIST') ?? true,
          },
          { root: true },
        );
      } else if (wentOffline) {
        NodeTreeHelper.clearExchangeInterval();
      }
    },
    updateNodeDataState(data) {
      this.nodeData = new NodeBasicInfoModel({ ...this.nodeData, data });
    },
    updateNodeInList(newNodeData) {
      if (newNodeData?.serialNumber) {
        const nodeFromMap = this.nodesMap.get(newNodeData.serialNumber);
        if (nodeFromMap) {
          this.nodesMap.set(newNodeData.serialNumber, new NodeModel(newNodeData));
        }
      }
    },
    removeOffboardedNodeFromList({ serialNumber, action }) {
      this.nodesMap.delete(serialNumber);
      if (this.selectedNodeSerialNumber !== serialNumber) {
        return;
      }
      this.selectedNodeSerialNumber = '';
      this.nodeData = new NodeModel({});

      const utilsStore = useUtilsStore();
      utilsStore.showCustomToast({
        text:
          action === 'offboard'
            ? i18n.global.t('errorMessages.nodeOffboarded', { serialNumber })
            : i18n.global.t('errorMessages.nodeRemovedFromSystem', { serialNumber }),
        color: 'red',
        showClose: true,
      });
    },
    handleNodeLifecycleEvents({ event, node, details }) {
      switch (event) {
        case 'offboarding':
          this.removeOffboardedNodeFromList({ serialNumber: node.serialNumber, action: details.action });
          break;
        case 'onboarding': {
          if (this.nodesMap.has(node.serialNumber)) {
            return;
          }
          const newNode = new NodeModel(node);
          this.nodesMap.set(newNode.serialNumber, newNode);
          break;
        }
      }
    },
    async assignLabelsToNode({ serialNumber, labels }) {
      const response = await NodesApiService.assignLabelsToNode({ serialNumber, labels });

      this.nodeData = new NodeBasicInfoModel({ ...this.nodeData, labels: response.labels });

      const nodeFromMap = this.nodesMap.get(serialNumber);
      if (nodeFromMap) {
        this.nodesMap.set(serialNumber, new NodeModel({ ...nodeFromMap, labels: response.labels }));
      }
      return response;
    },
    async unassignLabelsFromNode({ serialNumber, labelIds }) {
      const response = await NodesApiService.unassignLabelsFromNode({ serialNumber, labelIds });

      this.nodeData = new NodeBasicInfoModel({ ...this.nodeData, labels: response.labels });

      const nodeFromMap = this.nodesMap.get(serialNumber);
      if (nodeFromMap) {
        this.nodesMap.set(serialNumber, new NodeModel({ ...nodeFromMap, labels: response.labels }));
      }
      return response;
    },
    async importLabelsToNode({ serialNumber, formData }) {
      const response = await NodesApiService.importLabels({ serialNumber, formData });

      this.nodeData = new NodeBasicInfoModel({ ...this.nodeData, labels: response.labels });

      const nodeFromMap = this.nodesMap.get(serialNumber);
      if (nodeFromMap) {
        this.nodesMap.set(serialNumber, new NodeModel({ ...nodeFromMap, labels: response.labels }));
      }
      return response;
    },
    async exportLabels({ serialNumber }) {
      const exportUrl = NodesApiService.getLabelsExportUrl(serialNumber);
      const fullUrl = `${window.location.pathname.split('/')[0]}/${exportUrl}`;
      await checkUrlAndRedirect({
        url: fullUrl,
        location: true,
        cb: () => {
          window.location = fullUrl;
        },
      });
    },
    updateNodeDataLabelAndNodesMapLabel({ serialNumber, labelId, value }) {
      if (this.nodeData?.serialNumber === serialNumber) {
        const labelInData = this.nodeData?.labels?.find((l) => l._id === labelId);
        if (labelInData) {
          labelInData.value = value;
        }
      }
      const node = this.nodesMap.get(serialNumber);
      if (node?.labels?.some((l) => l._id === labelId)) {
        const updatedLabels = node.labels.map((l) => (l._id === labelId ? { ...l, value } : l));
        this.nodesMap.set(serialNumber, new NodeModel({ ...node, labels: updatedLabels }));
      }
    },
  },
});

export default useNodesStore;
